import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { EStreamProtocol, EStreamState, useStream } from '@/shared/streaming';
import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import type { V2BookConnectionDescriptor } from '@/generated/hooks/bdk/models';
import {
  getFailedConditions,
  getLastScannedTime,
} from '../components/connection-diagnostics-utils';

/**
 * The Rain agent that explains connection failures. Same agent used by the
 * "Ask me questions" integration assistant (see use-integration-assistant.ts).
 *
 * Insights are persisted, not one-shot: each is generated into a Rain thread
 * whose title encodes the connection id + a state version. On load we look up
 * that thread and replay its stored answer (a single GET, no generation). Only
 * when no thread exists for the current state do we create one and stream. This
 * keeps page loads instant and only invokes shifu once per connection state.
 */
const RAIN_AGENT_ID = 'shifu';
const RAIN_BASE = '/api/v1';

/** Rain event shape (only the fields we consume), shared by SSE + GetEvents. */
interface IRainEvent {
  id?: string;
  output_text?: { text?: string };
  tool_call_start?: { id?: string };
  generation_complete?: { id?: string };
  generation_failed?: { message?: string };
}

interface IRainThread {
  thread_id?: string;
  title?: string;
}

interface IUseConnectionDiagnosticsInsightReturn {
  /** The natural-language insight from shifu (streams in when freshly generated). */
  insight: string;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Builds the parent-path-routed agent base. The org/workspace/book/version
 * segments feed Rain's thread-variable resolver and satisfy the gateway's
 * role-management middleware, so they must be present on every request. See
 * use-integration-assistant.ts for the full rationale.
 */
function rainAgentBase(
  organizationId: string,
  workspaceId: string,
  bookName: string,
  bookVersion: string,
  connectionId: string,
): string {
  const seg = (s: string) => encodeURIComponent(s);
  // Route the thread under /connections/{id} so shifu resolves the
  // connection_id thread variable and enters diagnosis mode — it fetches the
  // live status via get_connection instead of us serializing it into the
  // prompt. (The gateway HTTPRoute has separate patterns for /authentications/
  // and /connections/; a path with both wouldn't match, so we route by
  // connection only.)
  return (
    `${RAIN_BASE}` +
    `/organizations/${seg(organizationId)}` +
    `/workspaces/${seg(workspaceId)}` +
    `/books/${seg(bookName)}` +
    `/versions/${seg(bookVersion)}` +
    `/connections/${seg(connectionId)}` +
    `/agents/${RAIN_AGENT_ID}`
  );
}

/** Finds prior diagnostics threads for this exact connection state, by title. */
async function rainFindThreads(
  agentBase: string,
  title: string,
): Promise<IRainThread[]> {
  // Escape backslashes first, then double quotes, so an unusual id/timestamp
  // can't malform (or inject into) the CEL filter — which would otherwise
  // silently match nothing and regenerate every load.
  const safeTitle = title.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const response = await axios.get<{ threads?: IRainThread[] }>(
    `${agentBase}/threads`,
    { params: { filter: `title == "${safeTitle}"` } },
  );
  return response.data?.threads ?? [];
}

/** Reads a thread's stored events (chronological). */
async function rainGetEvents(
  agentBase: string,
  threadId: string,
): Promise<IRainEvent[]> {
  const response = await axios.get<{ events?: IRainEvent[] }>(
    `${agentBase}/threads/${threadId}/events`,
  );
  return response.data?.events ?? [];
}

async function rainCreateThread(
  agentBase: string,
  title: string,
): Promise<string | null> {
  const response = await axios.post<{ thread_id?: string }>(
    `${agentBase}/threads`,
    { title },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return response.data?.thread_id ?? null;
}

async function rainSendMessage(
  agentBase: string,
  threadId: string,
  content: string,
): Promise<void> {
  await axios.post(
    `${agentBase}/threads/${threadId}/input`,
    {
      agent_id: RAIN_AGENT_ID,
      thread_id: threadId,
      message: { content },
    },
    { headers: { 'Content-Type': 'application/json' } },
  );
}

/** Reconstructs the agent's answer by concatenating output_text events. */
function joinOutputText(events: IRainEvent[]): string {
  return events.map(e => e.output_text?.text ?? '').join('');
}

/**
 * Prompt for shifu. The connection is pinned via the /connections/{id} thread
 * variable, so shifu fetches the live status itself with get_connection — we
 * no longer serialize the status into the prompt. This just asks for the
 * explanation in the strict two-part format the card parses.
 */
function buildPrompt(displayName: string): string {
  return [
    `The "${displayName}" connection is failing. Look up its current health and explain the failure.`,
    `\nRespond for a non-technical user in TWO parts separated by a line containing only:\n===FIXES===\nBefore the separator: ONE short sentence explaining what went wrong. After the separator: 1-2 short bullet points with the fix. Under 60 words total. Plain prose only — do NOT write your own section titles/headings, and do NOT use code blocks, backticks, or long URLs/code examples (describe the change in words instead). No preamble, no restating the question, no raw stack traces.`,
  ].join('\n');
}

/**
 * Stable, deterministic thread title = connection id + state version. The state
 * version is the latest condition transition (falling back to last_updated), so
 * a new failure state yields a new title (fresh generation), while an unchanged
 * state reuses the stored thread.
 */
function diagnosticsTitle(connection: V2BookConnectionDescriptor): string {
  const version =
    getLastScannedTime(connection.status) ??
    connection.status?.last_updated ??
    'unknown';
  return `conn-diagnostics/${connection.connection_id ?? 'unknown'}/${version}`;
}

/**
 * In-memory cache so repeat opens within a session skip even the lookup GETs.
 * Keyed by the same title used for the persisted thread. Bounded so a long
 * session with many failing connections can't grow it without limit (evicts
 * the oldest entry past the cap — Map preserves insertion order).
 */
const INSIGHT_CACHE_MAX = 50;
const insightCache = new Map<string, string>();

function cacheInsight(key: string, value: string): void {
  insightCache.set(key, value);
  if (insightCache.size > INSIGHT_CACHE_MAX) {
    const oldest = insightCache.keys().next().value;
    if (oldest !== undefined) insightCache.delete(oldest);
  }
}

/**
 * Asks the shifu Rain agent to explain a failing book connection and recommend
 * actions, reusing a previously generated answer when one exists for the same
 * connection state. Only runs when `enabled` and the connection is failing.
 */
export function useConnectionDiagnosticsInsight({
  connection,
  displayName,
  enabled,
}: {
  connection?: V2BookConnectionDescriptor;
  displayName?: string;
  enabled: boolean;
}): IUseConnectionDiagnosticsInsightReturn {
  const { getAccessTokenSilently } = useAuth();
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const bookName = connection?.book_name;
  const bookVersion = connection?.book_version;
  const connectionId = connection?.connection_id;

  const hasFailure = getFailedConditions(connection?.status).length > 0;
  const title = connection ? diagnosticsTitle(connection) : '';
  const cached = title ? insightCache.get(title) : undefined;

  const canRun = Boolean(
    enabled &&
      hasFailure &&
      connection &&
      organizationId &&
      workspaceId &&
      bookName &&
      bookVersion &&
      connectionId,
  );

  const [insight, setInsight] = useState(cached ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  // The state above outlives a connection switch when a dialog reuses this
  // hook, so it is only returned for the title it was produced for.
  const [stateTitle, setStateTitle] = useState('');

  const [threadId, setThreadId] = useState<string | null>(null);
  const threadIdRef = useRef<string | null>(null);
  threadIdRef.current = threadId;
  const sentRef = useRef(false);
  const bufferRef = useRef('');
  // True between a `tool_call_start` and the intermediate `generation_complete`
  // that closes shifu's tool-call pass, so we can tell that completion apart
  // from the real end-of-turn one (see handleRainEvent).
  const toolCallPendingRef = useRef(false);
  // Bumped on every fresh generation. The fallback thread re-read captures the
  // current value and bails if it changed by the time the GET resolves, so a
  // stale response can't clobber a newer generation's state (e.g. a status poll
  // landed a new title, or the popover reopened for another connection).
  const runIdRef = useRef(0);

  const agentBase = useMemo(
    () =>
      canRun
        ? rainAgentBase(
            organizationId,
            workspaceId,
            bookName as string,
            bookVersion as string,
            connectionId as string,
          )
        : null,
    [canRun, organizationId, workspaceId, bookName, bookVersion, connectionId],
  );

  const prompt = useMemo(
    () => (connection ? buildPrompt(displayName ?? bookName ?? '') : ''),
    [connection, displayName, bookName],
  );

  // Look up a prior answer for this connection state; generate only if missing.
  // eslint-disable-next-line no-restricted-syntax -- reason: Rain thread lookup/create is an imperative async flow
  useEffect(() => {
    if (!canRun || !agentBase) return;
    setStateTitle(title);
    if (cached) {
      setInsight(cached);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setInsight('');
    bufferRef.current = '';
    sentRef.current = false;
    toolCallPendingRef.current = false;
    runIdRef.current += 1;
    setThreadId(null);

    void (async () => {
      // 1. Try to reuse a stored answer. A failed lookup degrades to generating.
      try {
        const threads = await rainFindThreads(agentBase, title);
        const existing = threads.find(t => t.title === title && t.thread_id);
        if (existing?.thread_id) {
          const events = await rainGetEvents(agentBase, existing.thread_id);
          const text = joinOutputText(events);
          if (text) {
            if (cancelled) return;
            cacheInsight(title, text);
            setInsight(text);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // Ignore and fall through to generation.
      }
      if (cancelled) return;

      // 2. No reusable answer — create a thread and stream a fresh one.
      try {
        const id = await rainCreateThread(agentBase, title);
        if (cancelled) return;
        if (!id) {
          setIsError(true);
          setIsLoading(false);
          return;
        }
        setThreadId(id);
      } catch {
        if (!cancelled) {
          setIsError(true);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canRun, agentBase, title, cached]);

  const handleRainEvent = useCallback(
    (event: IRainEvent) => {
      if (event.output_text) {
        bufferRef.current += event.output_text.text ?? '';
        setInsight(bufferRef.current);
        return;
      }
      // shifu answers diagnosis/discovery by first calling a tool
      // (`get_connection`). Rain emits a `generation_complete` at the end of
      // that first pass — BEFORE the tool result comes back and the real answer
      // streams. Remember that a tool call is in flight so we don't mistake that
      // intermediate completion for the end of the turn (see below).
      if (event.tool_call_start) {
        toolCallPendingRef.current = true;
        return;
      }
      if (event.generation_complete) {
        const streamed = bufferRef.current;

        // No text yet, but a tool call was just made: this is the pre-answer
        // completion of the tool-call pass, not the end of the turn. The actual
        // answer (and a final `generation_complete`) still follows once the tool
        // result is fed back. Keep the stream open and wait for it — tearing
        // down here is what left the panel blank ("loader → nothing").
        if (!streamed && toolCallPendingRef.current) {
          toolCallPendingRef.current = false;
          return;
        }

        // Generation reached a terminal state — drop the thread id so the
        // stream URL goes null and useStream tears the SSE down instead of
        // holding (and retrying) a connection to a finished thread.
        const finishedThreadId = threadIdRef.current;
        setThreadId(null);

        if (streamed) {
          cacheInsight(title, streamed);
          setInsight(streamed);
          setIsLoading(false);
          return;
        }

        // The completion arrived but no text landed over the live stream.
        // Individual `output_text` tokens can be dropped or coalesced away by
        // an intermediary (gateway/proxy), leaving the buffer empty even though
        // the agent did answer — which is why the diagnosis only appeared after
        // a page refresh (a reload reads the finished thread over GET, not SSE).
        // Recover the answer the same way here: re-read the thread's events and
        // commit the joined text, keeping the loader up until it settles.
        if (!agentBase || !finishedThreadId) {
          setIsLoading(false);
          return;
        }
        const runId = runIdRef.current;
        void (async () => {
          try {
            const events = await rainGetEvents(agentBase, finishedThreadId);
            // A newer generation started while we were fetching (title changed,
            // popover reopened, etc.) — drop this stale result rather than
            // overwrite the fresher state or clear its loader.
            if (runId !== runIdRef.current) return;
            const text = joinOutputText(events);
            if (text) {
              cacheInsight(title, text);
              setInsight(text);
            }
          } catch {
            if (runId !== runIdRef.current) return;
            setIsError(true);
          } finally {
            if (runId === runIdRef.current) setIsLoading(false);
          }
        })();
        return;
      }
      if (event.generation_failed) {
        setIsError(true);
        setIsLoading(false);
        setThreadId(null);
      }
    },
    [title, agentBase],
  );

  // Only stream while a thread is actively generating; cleared on terminal
  // events (see handleRainEvent) so we never keep an SSE open to a done thread.
  const streamUrl =
    agentBase && threadId ? `${agentBase}/threads/${threadId}/stream` : null;

  const { state } = useStream<IRainEvent>(
    streamUrl
      ? {
          url: streamUrl,
          protocol: EStreamProtocol.SSE,
          auth: {
            getToken: () => getAccessTokenSilently(),
            e2eTestMode: isE2ETestMode(),
          },
          observability: {
            module: 'integrations',
            operation: 'connection_diagnostics_insight',
          },
          onMessage: handleRainEvent,
        }
      : null,
  );

  // Send the diagnostics once the SSE is connected, so no early tokens are lost.
  // eslint-disable-next-line no-restricted-syntax -- reason: send must wait for the stream to connect
  useEffect(() => {
    let cancelled = false;
    if (
      state === EStreamState.CONNECTED &&
      agentBase &&
      threadIdRef.current &&
      !sentRef.current
    ) {
      sentRef.current = true;
      rainSendMessage(agentBase, threadIdRef.current, prompt).catch(() => {
        // The cleanup below flips `cancelled` on unmount or when the effect
        // re-runs, so a late rejection (e.g. the popover closed mid-flight)
        // never calls setState on a stale/unmounted component.
        if (cancelled) return;
        setIsError(true);
        setIsLoading(false);
      });
    }
    // Cleanup: mark this run cancelled so an in-flight send's rejection is
    // ignored (see the guard in the .catch above).
    return () => {
      cancelled = true;
    };
  }, [state, agentBase, prompt]);

  const isCurrent = canRun && stateTitle === title;
  return {
    insight: cached ?? (isCurrent ? insight : ''),
    // Before the effect claims a new title, a runnable lookup is about to start.
    isLoading: !cached && canRun && (isCurrent ? isLoading : true),
    isError: isCurrent && isError,
  };
}
